/**
 * Money helpers. The API always sends integer **kobo**; the UI shows naira.
 * Never do arithmetic on naira floats — convert to kobo first.
 */
export const KOBO_PER_NAIRA = 100;

const grouped = new Intl.NumberFormat("en-NG", { maximumFractionDigits: 0 });

/** formatNaira(350000) → "₦3,500"; kobo only shown when non-zero (or `showKobo: true`). */
export function formatNaira(kobo: number, opts: { showKobo?: boolean; compact?: boolean } = {}): string {
  if (!Number.isFinite(kobo)) return "₦0";
  const negative = kobo < 0;
  const abs = Math.abs(Math.round(kobo));
  if (opts.compact && abs >= 100_000_000) {
    const naira = abs / KOBO_PER_NAIRA;
    const text =
      naira >= 1_000_000_000
        ? `${trim(naira / 1_000_000_000)}B`
        : naira >= 1_000_000
          ? `${trim(naira / 1_000_000)}M`
          : `${trim(naira / 1_000)}k`;
    return `${negative ? "-" : ""}₦${text}`;
  }
  const whole = Math.floor(abs / KOBO_PER_NAIRA);
  const frac = abs % KOBO_PER_NAIRA;
  const show = opts.showKobo ?? frac !== 0;
  const text = `₦${grouped.format(whole)}${show ? `.${String(frac).padStart(2, "0")}` : ""}`;
  return negative ? `-${text}` : text;
}

function trim(n: number): string {
  return n.toFixed(1).replace(/\.0$/, "");
}

/** toKobo("3,500.5") → 350050. Accepts numbers or user-typed strings with commas/₦. */
export function toKobo(naira: number | string): number {
  const cleaned = typeof naira === "number" ? String(naira) : naira.replace(/[₦,\s]/g, "");
  if (cleaned === "" || !/^-?\d*\.?\d*$/.test(cleaned)) return Number.NaN;
  const [whole = "0", frac = ""] = cleaned.replace(/^-/, "").split(".");
  const kobo = Number(whole || "0") * KOBO_PER_NAIRA + Number((frac + "00").slice(0, 2)) + (Number(frac[2] ?? 0) >= 5 ? 1 : 0);
  return cleaned.startsWith("-") ? -kobo : kobo;
}

/** Percentage discount between old and new price (kobo), rounded down; 0 when not a discount. */
export function discountPercent(priceKobo: number, oldPriceKobo?: number | null): number {
  if (!oldPriceKobo || oldPriceKobo <= priceKobo) return 0;
  return Math.floor(((oldPriceKobo - priceKobo) / oldPriceKobo) * 100);
}
